import { useState } from 'react';
import { line, Size, leftToPack, packableLines, t, type NewPackageRequest, type PackLineInput, type PackageFormAnswer } from '@bms2/shared';
import { DatePicker, NumericInput, Select } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { useDocumentToday } from '../../shell/ViewerZone';
import { PACKAGES_PATH, packageFormPath, packagePath, post, put, useApi } from './api';
import styles from './PackageWindows.module.css';

/**
 * 🔒 The package window (decision 1730), the New Package form of the owner's
 * Zoho screen: the date, the package type, the net and gross weight with their
 * unit, the three dimensions with theirs, and the quantity packed on each goods
 * line of the order, each line opening at what it has left to pack. Edit
 * reopens the same window on a Not Shipped package, keyed to it by its caller.
 *
 * 🔨 A Service line is never drawn: it ships nothing from a shelf.
 */
export function PackageModal({ salesOrderId, packageId = null, apiBaseUrl = '', onCancel, onSaved }: {
  salesOrderId: string;
  /** The package the window reopens; null packs a new one. */
  packageId?: string | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: (saved: { id: string; packageNumber: string }) => void;
}) {
  const form = useApi<PackageFormAnswer>(packageFormPath(salesOrderId, packageId), apiBaseUrl);
  const title = packageId ? t('package.window.editTitle') : t('package.window.newTitle');
  if (form.state.kind === 'ready') return <PackageWindow key={packageId ?? 'new'} title={title} answer={form.state.data} packageId={packageId} apiBaseUrl={apiBaseUrl} onCancel={onCancel} onSaved={onSaved} />;
  return (
    <Modal open title={title} onCancel={onCancel} dataModal="package" actions={[
      // 🔒 One Primary always (piece 5): Try again once the read failed, else greyed with the reason while it is in flight (decision 0559).
      form.state.kind === 'failed'
        ? { kind: 'primary', label: t('package.window.retry'), onAct: form.reload }
        : { kind: 'primary', label: packageId ? t('package.window.saveEdit') : t('package.window.save'), onAct: form.reload, disabled: true, disabledReason: t('package.window.loading') },
      { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
    ]}>
      {form.state.kind === 'failed' ? <AlertBanner tone="error" message={form.state.error} /> : <p className={modalStyles.fieldLabel}>{t('package.window.loading')}</p>}
    </Modal>
  );
}

/** The window itself, mounted once its read has answered, so each field opens on the stored package or the defaults. */
function PackageWindow({ title, answer, packageId, apiBaseUrl, onCancel, onSaved }: {
  title: string;
  answer: PackageFormAnswer;
  packageId: string | null;
  apiBaseUrl: string;
  onCancel: () => void;
  onSaved: (saved: { id: string; packageNumber: string }) => void;
}) {
  const today = useDocumentToday();
  const current = answer.current;
  const goods = answer.lines.filter((row) => row.itemId && !row.isService);
  const opening: Record<string, number | null> = Object.fromEntries(
    (current ? current.lines : packableLines(answer.lines)).map((row: PackLineInput) => [row.sourceLineId, row.quantity]),
  );
  const [packed, setPacked] = useState<Record<string, number | null>>(opening);
  const [packageDate, setPackageDate] = useState(current?.packageDate ?? today);
  const [packageTypeId, setPackageTypeId] = useState(current?.packageTypeId ?? answer.packageTypes[0]?.id ?? '');
  const [netWeight, setNetWeight] = useState<number | null>(current?.netWeight ?? null);
  const [grossWeight, setGrossWeight] = useState<number | null>(current?.grossWeight ?? null);
  const [weightUnitId, setWeightUnitId] = useState(current?.weightUnitId ?? answer.defaultWeightUnitId ?? '');
  const [length, setLength] = useState<number | null>(current?.length ?? null);
  const [width, setWidth] = useState<number | null>(current?.width ?? null);
  const [height, setHeight] = useState<number | null>(current?.height ?? null);
  const [dimensionUnitId, setDimensionUnitId] = useState(current?.dimensionUnitId ?? answer.defaultDimensionUnitId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const lines = Object.entries(packed).flatMap(([sourceLineId, quantity]) => (quantity !== null && quantity > 0 ? [{ sourceLineId, quantity }] : []));
    if (lines.length === 0) return setError(t('package.window.needsLine'));
    if (!packageDate) return setError(t('package.window.needsDate'));
    const body: Omit<NewPackageRequest, 'salesOrderId'> = {
      packageDate, packageTypeId: packageTypeId || null, netWeight, grossWeight, weightUnitId: weightUnitId || null, length, width, height, dimensionUnitId: dimensionUnitId || null, lines,
    };
    setBusy(true);
    const saved = packageId
      ? await put<{ id: string; packageNumber: string }>(packagePath(packageId), { ...body, version: current?.version ?? 0 }, apiBaseUrl)
      : await post<{ id: string; packageNumber: string }>(PACKAGES_PATH, { ...body, salesOrderId: answer.salesOrderId }, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    onSaved(saved.data);
  };

  const unitOptions = (rows: readonly { id: string; label: string }[]) => rows.map((row) => ({ value: row.id, label: row.label }));
  return (
    <Modal
      open
      title={title}
      subtitle={line(t('package.window.forOrder'), answer.salesOrderNumber)}
      onCancel={onCancel}
      dataModal="package"
      width="wide"
      actions={[
        { kind: 'primary', label: packageId ? t('package.window.saveEdit') : t('package.window.save'), onAct: () => void save(), disabled: busy },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <div className={modalStyles.pair}>
        <DatePicker size={Size.Small} label={t('package.window.date')} value={packageDate} onChange={setPackageDate} mode="date" name="packageDate" required />
        <Select size={Size.Medium} label={t('package.window.type')} value={packageTypeId} onChange={setPackageTypeId} placeholder={t('customFieldsModal.none')} options={answer.packageTypes.map((row) => ({ value: row.id, label: row.label }))} dataAttribute={{ 'data-package-field': 'type' }} />
      </div>
      <div className={modalStyles.pair}>
        <NumericInput size={Size.Small} label={t('package.window.netWeight')} kind="number" decimals={3} value={netWeight} onChange={setNetWeight} name="netWeight" />
        <NumericInput size={Size.Small} label={t('package.window.grossWeight')} kind="number" decimals={3} value={grossWeight} onChange={setGrossWeight} name="grossWeight" />
        <Select size={Size.Small} label={t('package.window.weightUnit')} value={weightUnitId} onChange={setWeightUnitId} options={unitOptions(answer.weightUnits)} dataAttribute={{ 'data-package-field': 'weightUnit' }} />
      </div>
      <div className={modalStyles.pair}>
        <NumericInput size={Size.Small} label={t('package.window.length')} kind="number" decimals={3} value={length} onChange={setLength} name="length" />
        <NumericInput size={Size.Small} label={t('package.window.width')} kind="number" decimals={3} value={width} onChange={setWidth} name="width" />
        <NumericInput size={Size.Small} label={t('package.window.height')} kind="number" decimals={3} value={height} onChange={setHeight} name="height" />
        <Select size={Size.Small} label={t('package.window.dimensionUnit')} value={dimensionUnitId} onChange={setDimensionUnitId} options={unitOptions(answer.dimensionUnits)} dataAttribute={{ 'data-package-field': 'dimensionUnit' }} />
      </div>
      <p className={modalStyles.fieldLabel}>{t('package.window.linesHint')}</p>
      {goods.length === 0 ? <p className={modalStyles.fieldLabel} data-package-no-lines="true">{t('package.window.noGoods')}</p> : null}
      <ul className={styles.lines} data-package-lines={goods.length}>
        {goods.map((row) => {
          const left = leftToPack(row);
          return (
            <li key={row.id} className={styles.line} data-package-line={row.id} data-package-left={left}>
              <span className={styles.label}>
                <span className={styles.name}>{row.sku ? line(row.sku, '·', row.description) : row.description}</span>
                <span className={styles.remaining}>{line(t('package.window.ordered'), String(row.quantity), row.unitName, '·', t('package.window.packedElsewhere'), String(row.packedElsewhere), '·', String(left), t('package.window.left'))}</span>
              </span>
              <NumericInput size={Size.Small} label={t('package.window.quantity')} kind="number" decimals={2} value={packed[row.id] ?? null} onChange={(next) => setPacked((was) => ({ ...was, [row.id]: next }))} name={`pack-${row.id}`} disabled={left <= 0 && !(packed[row.id] ?? 0)} />
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
